import React from 'react'
import { useAtomValue } from 'jotai'
import styled from 'styled-components'
import dt from 'dayjs'
import * as state from '../../states'
import { ERoleAccess, IRevenueSpend } from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconDeleteBin,
	IconEdit,
	headCellFormatterSortable,
	ESortOrder,
	HistoryIconBtn,
	NotesIconBtn
} from '../ui'
import { REVENUE_SPEND_PAGE_SORT_FIELDS, IRevenueSpendSortField } from '../../hooks'
import { Access } from '../../lib'
import * as helpers from '../../helpers'
import { ALL_LANG_KEY } from '../../types/lang'
import { useTranslation } from 'react-i18next'

type IRevenueSpendListTableProps = {
	items: IRevenueSpend[];
	skip: number;
	sortBy: IRevenueSpendSortField;
	setSortBy: (sortBy: IRevenueSpendSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onEdit?: (item: IRevenueSpend, e: React.MouseEvent) => void;
	onEditNotes?: (item: IRevenueSpend, e: React.MouseEvent) => void;
	onRemove?: (item: IRevenueSpend, e: React.MouseEvent) => void;
	onViewHistory?: (item: IRevenueSpend, e: React.MouseEvent) => void;
}

export const RevenueSpendListTable = (props: IRevenueSpendListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onEdit,
		onEditNotes,
		onRemove,
		onViewHistory
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const revenueSpendListTableConfig: IUiTableConfig<IRevenueSpend>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_id)
		},
		{
			key: 'at',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_date),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const at = rowData?.at ?? null
				return <>
					{dt.utc(at).isValid() ? dt.utc(at).format('L') : '-'}
				</>
			}
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_amount),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const formatValue = helpers.formatAmount(value)

				return <>{helpers.thousandSeparator(formatValue)}</>
			}
		},
		{
			key: 'accountId',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_account),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const accountId = rowData?.accountId ?? null
				const account = rowData?.account ?? null
				// const email = account?.email ?? null
				const auth = account?.auth ?? null
				const login = auth?.login ?? null
				return <>
					<strong>[id: {accountId}]</strong>
					&nbsp;
					{login ?? '?'}
				</>
			}
		},
		{
			key: 'account.extra.keitaroLogin',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_kt_group),
			rowCellFormatter: ({ rowData }) => {
				const extra = rowData.account?.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'agencyId',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_agency),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const agencyId = rowData?.agencyId ?? null
				const agency = rowData?.agency ?? null
				const name = agency?.name ?? null

				return <>
					<strong>[id: {agencyId}]</strong>
					&nbsp;
					{name ?? '?'}
				</>
			}
		},
		{
			key: 'history.empty',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_history),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				const val = rowData?.history

				return <>
					{
						onViewHistory && !!$access.$(ERoleAccess.RevenueSpendList)
							? (
								<HistoryIconBtn value={val}
								                onClick={(e) => onViewHistory && onViewHistory(rowData, e)}
								/>
							)
							: null
					}
				</>
			}
		},
		{
			key: 'notes',
			title: t(ALL_LANG_KEY.revenue_spend_list_table.column_notes),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				const val = rowData?.notes

				return <>
					{
						onEditNotes && !!$access.$(ERoleAccess.RevenueSpendList)
							? (
								<NotesIconBtn value={val}
								              onClick={(e) => onEditNotes && onEditNotes(rowData, e)}
								/>
							)
							: null
					}
				</>
			}
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				const at = dt.utc(rowData.at)
				const hasRevenueSpendAnyDay = $access.$(ERoleAccess.RevenueSpendAnyDay)
				const isYesterday = dt.utc(at).startOf('day').isYesterday()
				const isToday = dt.utc(at).startOf('day').isToday()
				const disabled = !hasRevenueSpendAnyDay && !isYesterday && !isToday
				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>
						{
							!!onEdit && !!$access.$(ERoleAccess.RevenueSpendModify) && !disabled
								? (
									<button
										name="edit"
										className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
										onClick={(e) => onEdit && onEdit(rowData, e)}
									>
										<IconEdit/>
									</button>
								)
								: null
						}

						{
							!!onRemove && !!$access.$(ERoleAccess.RevenueSpendRemove) && !disabled
								? (
									<button
										name="delete"
										className="icon border-0 rounded-circle text-center trash bg-danger-transparent delete-item"
										onClick={(e) => onRemove && onRemove(rowData, e)}
									>
										<IconDeleteBin/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const revenueSpendListTableConfigSortable: IUiTableConfig<IRevenueSpend>[] = revenueSpendListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = REVENUE_SPEND_PAGE_SORT_FIELDS.includes(configItem.key as IRevenueSpendSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		}
	)

	//console.log('RevenueSpendListTable:: ', { pendListTableConfig, roleListTableConfigSortable })

	return <>
		<UiTable
			data={items}
			config={revenueSpendListTableConfigSortable}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}